import type { RaidScheduleDto } from '@/api/hooks'

/**
 * Recon legibility for the next NPC raid. The backend gates the intel behind the
 * Watchtower level (see `WatchtowerIntel`): the floor (who + a fuzzy timing
 * bucket) is always known, while danger, composition, repel% and the precise ETA
 * unlock tier by tier. These helpers turn the raw payload into player-facing copy
 * — shared by the DefensePanel NextRaid card and the Map incoming-attack panel so
 * the threat reads the same everywhere.
 */

/** Fuzzy timing label shown when the Watchtower hasn't revealed the precise ETA. */
export function scheduleBucketLabel(bucket: RaidScheduleDto['bucket']): string {
  switch (bucket) {
    case 'imminent':
      return 'Imminent'
    case 'bientot':
      return 'Bientôt'
    case 'lointain':
      return 'Lointain'
    default:
      return 'Bientôt'
  }
}

export interface RepelReading {
  /** Win chance rounded to a percent (0..100). */
  percent: number
  /** Short qualitative reading of the chance. */
  label: string
  /** Tailwind text-colour class matching the severity of the reading. */
  tone: string
}

/**
 * Turn a defender win fraction (0..1) into a percent + a short reading. Thresholds:
 *   ≥ 0.60  "tes murs devraient tenir"  (green)
 *   0.40..0.60  "issue incertaine"      (amber)
 *   < 0.40  "tes murs sont dépassés"    (red)
 */
export function repelReading(estimate: number): RepelReading {
  const percent = Math.round(estimate * 100)
  if (estimate >= 0.6) {
    return { percent, label: 'tes murs devraient tenir', tone: 'text-emerald-300' }
  }
  if (estimate >= 0.4) {
    return { percent, label: 'issue incertaine', tone: 'text-amber-300' }
  }
  return { percent, label: 'tes murs sont dépassés', tone: 'text-red-400' }
}
